import React, { useState, useEffect, useMemo } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
type Categoria = string;
type Ubicacion = 'FRIGORÍFICO GABOTO' | 'FÁBRICA';

interface StockMP {
    id: string;
    categoria: Categoria;
    subcategoria: string;
    producto: string;
    kg: number;
    unidades: number;
    bolsas?: number;
    ubicacion: Ubicacion;
    freezer: string;
    caja?: number;
    fecha: string;
    tipo?: 'entrada' | 'salida';
    motivo?: string;
}

interface StockMI {
    id: string;
    producto: string;
    // Materia prima utilizada
    kgMP: number;
    unidadesMP: number;
    // Producto intermedio obtenido
    kg: number;
    unidades: number;
    ubicacion: Ubicacion;
    freezer: string;
    origenProducto: string;
    fecha: string;
    tipo?: 'entrada' | 'salida';
    motivo?: string;
}

// ─── Static data ─────────────────────────────────────────────────────────────
const CATEGORIAS_DATA: Record<Categoria, Record<string, string[]>> = {
    NOVILLO: {
        'Mila común': ['Nalga', 'Cuadrada', 'Bola de lomo', 'Cuadril', 'Brazuelo', 'Tapa de nalga'],
        'Mila prem': ['Colita de cuadril', 'Ceja', 'Bife Ancho', 'Bife Angosto', 'Entraña', 'Lomo', 'Peceto', 'Picaña'],
        'Resto': ['Recortes', 'Garron', 'Azotillo', 'Palomita', 'Vacío', 'Tapa de asado', 'Tapa de nalga', 'Entrecot común', 'Asado', 'Tortuguita'],
        'Huesos y grasa': ['Huesos de novillos crudos', 'Grasa novillo común', 'Grasa novillo pella'],
    },
    POLLO: {
        'Pollo': ['Huesos de pollo crudos', 'Piel de pollo cruda', 'Alitas crudas', 'Drummets crudos'],
    },
    CORDERO: {
        'Cordero': ['Hueso de cordero crudos', 'Grasa de cordero cruda'],
    },
};

const FREEZERS_FABRICA = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9'];
const PALLETS_GABOTO = ['Pallet 1', 'Pallet 2'];

const MATERIAS_INTERMEDIAS = [
    'Caldo de pollo', 'Caldo de novillo', 'Caldo de cordero',
    'Alitas rebozadas', 'Drummets rebozados', 'Cogotes',
    'Pancitas', 'Hígado', 'Corazón',
];

const LS_KEY_MP = 'stock_materia_prima_v1';
const LS_KEY_MI = 'stock_materia_intermedia_v1';
const LS_KEY_CATS_MP = 'crud_cats_mp_v1';
const LS_KEY_SUBCATS_MP = 'crud_subcats_mp_v1';
const LS_KEY_PRODS_MP = 'crud_productos_mp_v1';
const LS_KEY_PRODS_MI = 'crud_productos_mi_v1';
// Marca de que ya se migró el localStorage al backend (para no repetir la migración)
const LS_KEY_MIGRATED = 'stockfabrica_migrado_backend_v1';

// ─── API backend ──────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const SF_BASE = `${API_URL}/admin/stockfabrica`;

const sfApi = {
    getMP: () => fetch(`${SF_BASE}/mp`).then(r => r.json()),
    addMP: (items: any) => fetch(`${SF_BASE}/mp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(items),
    }),
    delMP: (id: string) => fetch(`${SF_BASE}/mp/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    getMI: () => fetch(`${SF_BASE}/mi`).then(r => r.json()),
    addMI: (items: any) => fetch(`${SF_BASE}/mi`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(items),
    }),
    delMI: (id: string) => fetch(`${SF_BASE}/mi/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    getCatalogo: () => fetch(`${SF_BASE}/catalogo`).then(r => r.json()),
    putCatalogo: (payload: any) => fetch(`${SF_BASE}/catalogo`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    }),
};

interface CatMPItem { id: string; nombre: string; }
interface SubcatMPItem { id: string; categoria: string; nombre: string; }
interface ProductoMPItem { id: string; categoria: string; subcategoria: string; nombre: string; }
interface ProductoMIItem { id: string; nombre: string; }

// Seed helpers (convierte los datos hardcodeados en arrays planos al primer uso)
const seedCatsMP = (): CatMPItem[] =>
    Object.keys(CATEGORIAS_DATA).map(n => ({ id: n, nombre: n }));

const seedSubcatsMP = (): SubcatMPItem[] => {
    const r: SubcatMPItem[] = [];
    (Object.keys(CATEGORIAS_DATA) as Categoria[]).forEach(cat =>
        Object.keys(CATEGORIAS_DATA[cat]).forEach(sub =>
            r.push({ id: `${cat}__${sub}`, categoria: cat, nombre: sub })
        )
    );
    return r;
};
const seedProductosMP = (): ProductoMPItem[] => {
    const r: ProductoMPItem[] = [];
    (Object.keys(CATEGORIAS_DATA) as Categoria[]).forEach(cat =>
        Object.keys(CATEGORIAS_DATA[cat]).forEach(sub =>
            CATEGORIAS_DATA[cat][sub].forEach(prod =>
                r.push({ id: `${cat}__${sub}__${prod}`, categoria: cat, subcategoria: sub, nombre: prod })
            )
        )
    );
    return r;
};
const seedProductosMI = (): ProductoMIItem[] =>
    MATERIAS_INTERMEDIAS.map(n => ({ id: n, nombre: n }));

// ─── Shared style helpers ─────────────────────────────────────────────────────
const selectStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.15)', fontSize: '0.9rem',
    backgroundColor: '#f8f9fa', color: '#000',
};
const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.15)', fontSize: '0.9rem',
    boxSizing: 'border-box', backgroundColor: '#f8f9fa', color: '#000',
};
const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px', color: '#f8f9fa',
};
const thStyle: React.CSSProperties = {
    padding: '10px 12px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap',
    backgroundColor: '#48111d', color: '#f39c12',
    textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.04em',
};
const tdStyle: React.CSSProperties = {
    padding: '8px 12px', color: '#f8f9fa',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
};

// ─── Custom Select Component ──────────────────────────────────────────────────
interface CustomSelectProps {
    value: string;
    onChange: (val: string) => void;
    options: string[];
    placeholder?: string;
    label?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ value, onChange, options, placeholder = '-- Seleccionar --' }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const containerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));

    return (
        <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div
                onClick={() => { setIsOpen(!isOpen); setSearch(''); }}
                style={{
                    ...selectStyle,
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '38px',
                    boxSizing: 'border-box'
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {value || placeholder}
                </span>
                <span style={{ fontSize: '0.7rem', color: 'rgba(0,0,0,0.5)' }}>{isOpen ? '▲' : '▼'}</span>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '0 0 6px 6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    marginTop: '2px',
                    maxHeight: '250px',
                    overflowY: 'auto'
                }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, backgroundColor: '#fff' }}>
                        <input
                            autoFocus
                            type="text"
                            placeholder="Buscar..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '6px 8px',
                                borderRadius: '4px',
                                border: '1px solid #ddd',
                                fontSize: '0.85rem',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    {filtered.length === 0 ? (
                        <div style={{ padding: '10px', fontSize: '0.85rem', color: '#999', textAlign: 'center' }}>No hay resultados</div>
                    ) : (
                        filtered.map(opt => (
                            <div
                                key={opt}
                                onClick={() => { onChange(opt); setIsOpen(false); }}
                                style={{
                                    padding: '8px 12px',
                                    fontSize: '0.85rem',
                                    color: '#333',
                                    cursor: 'pointer',
                                    backgroundColor: value === opt ? '#f0f0f0' : 'transparent',
                                    transition: 'background 0.1s'
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7f7f7')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = value === opt ? '#f0f0f0' : 'transparent')}
                            >
                                {opt}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Component ────────────────────────────────────────────────────────────────
const StockFabrica: React.FC = () => {
    const [view, setView] = useState<'mp' | 'mi' | 'crud'>('mp');

    // ── Carga desde backend ──────────────────────────────────────────────────
    const [loaded, setLoaded] = useState(false);

    // ── Materia Prima state ──────────────────────────────────────────────────
    const [stockMP, setStockMP] = useState<StockMP[]>([]);

    const [mpCategoria, setMpCategoria] = useState<Categoria>('NOVILLO');
    const [mpSubcat, setMpSubcat] = useState('');
    const [mpProducto, setMpProducto] = useState('');
    const [mpKg, setMpKg] = useState('');
    const [mpUnidades, setMpUnidades] = useState('');
    const [mpUbicacion, setMpUbicacion] = useState<Ubicacion>('FÁBRICA');
    const [mpFreezer, setMpFreezer] = useState('');
    const [mpCaja, setMpCaja] = useState('');
    const [mpBolsas, setMpBolsas] = useState('');

    // ── Modal edit MP ────────────────────────────────────────────────────────
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [modalCategoria, setModalCategoria] = useState<Categoria>('NOVILLO');
    const [modalSubcat, setModalSubcat] = useState('');
    const [modalProducto, setModalProducto] = useState('');
    const [modalKg, setModalKg] = useState('');
    const [modalUnidades, setModalUnidades] = useState('');
    const [modalUbicacion, setModalUbicacion] = useState<Ubicacion>('FÁBRICA');
    const [modalFreezer, setModalFreezer] = useState('');
    const [modalCaja, setModalCaja] = useState('');

    const [filterCategoria, setFilterCategoria] = useState('');
    const [filterUbicacion, setFilterUbicacion] = useState('');

    // ── Materia Intermedia state ─────────────────────────────────────────────
    const [stockMI, setStockMI] = useState<StockMI[]>([]);

    const [miProducto, setMiProducto] = useState('');
    const [miKgMP, setMiKgMP] = useState('');
    const [miUnidadesMP, setMiUnidadesMP] = useState('');
    const [miKg, setMiKg] = useState('');
    const [miUnidades, setMiUnidades] = useState('');
    const [miUbicacion, setMiUbicacion] = useState<Ubicacion>('FÁBRICA');
    const [miFreezer, setMiFreezer] = useState('');
    const [miOrigen, setMiOrigen] = useState('');

    // ── Listas dinámicas (editables via CRUD) ────────────────────────────────
    // Se inicializan con los seeds para que los selects no aparezcan vacíos
    // mientras carga; la carga desde backend los reemplaza al montar.
    const [categoriasMP, setCategoriasMP] = useState<CatMPItem[]>(seedCatsMP);
    const [subcatsMP, setSubcatsMP] = useState<SubcatMPItem[]>(seedSubcatsMP);
    const [productosMP, setProductosMP] = useState<ProductoMPItem[]>(seedProductosMP);
    const [productosMI, setProductosMI] = useState<ProductoMIItem[]>(seedProductosMI);

    // ── Estado CRUD ──────────────────────────────────────────────────────────
    const [crudSection, setCrudSection] = useState<'mp' | 'mi'>('mp');
    const [crudCatFilter, setCrudCatFilter] = useState<Categoria>('NOVILLO');
    const [newCatName, setNewCatName] = useState('');
    const [editCatId, setEditCatId] = useState<string | null>(null);
    const [editCatName, setEditCatName] = useState('');
    const [newSubcatName, setNewSubcatName] = useState('');

    // ── Sub-tabs ─────────────────────────────────────────────────────────────
    const [mpTab, setMpTab] = useState<'form' | 'resumen' | 'cajas' | 'historial'>('form');
    const [miTab, setMiTab] = useState<'form' | 'resumen' | 'historial'>('form');

    // ── Salida por selección (MP) ────────────────────────────────────────────
    // Grupo de ubicación seleccionado para vaciar: "FÁBRICA|F1" o "FRIGORÍFICO GABOTO|Pallet 1|59"
    const [salidaGroupMP, setSalidaGroupMP] = useState<string | null>(null);
    // Cantidades a sacar por item: key = id de stock-vivo -> { kg, unidades, bolsas }
    const [salidaQtyMP, setSalidaQtyMP] = useState<Record<string, { kg: string; unidades: string; bolsas: string }>>({});
    const [salidaMotivoMP, setSalidaMotivoMP] = useState('');

    // ── Salida por selección (MI) ────────────────────────────────────────────
    const [salidaGroupMI, setSalidaGroupMI] = useState<string | null>(null);
    const [salidaQtyMI, setSalidaQtyMI] = useState<Record<string, { kg: string; unidades: string }>>({});
    const [salidaMotivoMI, setSalidaMotivoMI] = useState('');
    const [mpTipo, setMpTipo] = useState<'entrada' | 'salida'>('entrada');
    const [miTipo, setMiTipo] = useState<'entrada' | 'salida'>('entrada');
    const [mpMotivo, setMpMotivo] = useState('');
    const [miMotivo, setMiMotivo] = useState('');

    // ── Sorting state ────────────────────────────────────────────────────────
    const [sortMP, setSortMP] = useState<{ key: keyof StockMP; dir: 'asc' | 'desc' } | null>({ key: 'fecha', dir: 'desc' });
    const [sortMI, setSortMI] = useState<{ key: keyof StockMI; dir: 'asc' | 'desc' } | null>({ key: 'fecha', dir: 'desc' });
    const [crudProdCat, setCrudProdCat] = useState<Categoria>('NOVILLO');
    const [crudProdSubcat, setCrudProdSubcat] = useState('');
    const [newProdMpName, setNewProdMpName] = useState('');
    const [editProdMpId, setEditProdMpId] = useState<string | null>(null);
    const [editProdMpName, setEditProdMpName] = useState('');
    const [newProdMiName, setNewProdMiName] = useState('');
    const [editProdMiId, setEditProdMiId] = useState<string | null>(null);
    const [editProdMiName, setEditProdMiName] = useState('');

    // ── Carga inicial desde backend + migración única de localStorage ─────────
    useEffect(() => {
        let cancelado = false;
        (async () => {
            try {
                // Lee lo que pueda haber quedado en localStorage de la versión vieja
                const readLS = (k: string) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
                const lsMP: StockMP[] | null = readLS(LS_KEY_MP);
                const lsMI: StockMI[] | null = readLS(LS_KEY_MI);
                const lsCats: CatMPItem[] | null = readLS(LS_KEY_CATS_MP);
                const lsSubs: SubcatMPItem[] | null = readLS(LS_KEY_SUBCATS_MP);
                const lsProdMp: ProductoMPItem[] | null = readLS(LS_KEY_PRODS_MP);
                const lsProdMi: ProductoMIItem[] | null = readLS(LS_KEY_PRODS_MI);
                const yaMigrado = localStorage.getItem(LS_KEY_MIGRATED) === '1';

                const [mp, mi, cat] = await Promise.all([sfApi.getMP(), sfApi.getMI(), sfApi.getCatalogo()]);

                const backendVacio =
                    (!Array.isArray(mp) || mp.length === 0) &&
                    (!Array.isArray(mi) || mi.length === 0) &&
                    (!cat?.categorias || cat.categorias.length === 0);

                const hayLocal = (lsMP && lsMP.length) || (lsMI && lsMI.length) || (lsCats && lsCats.length);

                // Migración: si el backend está vacío, todavía no migramos y hay datos locales → subirlos una vez
                if (backendVacio && !yaMigrado && hayLocal) {
                    const catalogo = {
                        categorias: lsCats && lsCats.length ? lsCats : seedCatsMP(),
                        subcategorias: lsSubs && lsSubs.length ? lsSubs : seedSubcatsMP(),
                        productosMp: lsProdMp && lsProdMp.length ? lsProdMp : seedProductosMP(),
                        productosMi: lsProdMi && lsProdMi.length ? lsProdMi : seedProductosMI(),
                    };
                    await sfApi.putCatalogo(catalogo);
                    if (lsMP && lsMP.length) await sfApi.addMP(lsMP);
                    if (lsMI && lsMI.length) await sfApi.addMI(lsMI);
                    localStorage.setItem(LS_KEY_MIGRATED, '1');

                    if (!cancelado) {
                        setStockMP(lsMP || []);
                        setStockMI(lsMI || []);
                        setCategoriasMP(catalogo.categorias);
                        setSubcatsMP(catalogo.subcategorias);
                        setProductosMP(catalogo.productosMp);
                        setProductosMI(catalogo.productosMi);
                        setLoaded(true);
                    }
                    return;
                }

                // Si el backend no tiene catálogo todavía (primera vez, sin datos locales) → sembrarlo
                if (!cat?.categorias || cat.categorias.length === 0) {
                    const semilla = {
                        categorias: seedCatsMP(), subcategorias: seedSubcatsMP(),
                        productosMp: seedProductosMP(), productosMi: seedProductosMI(),
                    };
                    await sfApi.putCatalogo(semilla);
                    if (!cancelado) {
                        setCategoriasMP(semilla.categorias);
                        setSubcatsMP(semilla.subcategorias);
                        setProductosMP(semilla.productosMp);
                        setProductosMI(semilla.productosMi);
                    }
                } else if (!cancelado) {
                    setCategoriasMP(cat.categorias);
                    setSubcatsMP(cat.subcategorias || []);
                    setProductosMP(cat.productosMp || []);
                    setProductosMI(cat.productosMi || []);
                }

                if (!cancelado) {
                    setStockMP(Array.isArray(mp) ? mp : []);
                    setStockMI(Array.isArray(mi) ? mi : []);
                    setLoaded(true);
                }
            } catch (e) {
                console.error('Error cargando Stock de Fábrica desde backend:', e);
                if (!cancelado) setLoaded(true); // permitir trabajar igual (con seeds)
            }
        })();
        return () => { cancelado = true; };
    }, []);

    // ── Persistencia del catálogo en backend (reemplazo completo, debounced) ──
    useEffect(() => {
        if (!loaded) return; // no guardar mientras carga (evita pisar el backend con seeds)
        const t = setTimeout(() => {
            sfApi.putCatalogo({
                categorias: categoriasMP,
                subcategorias: subcatsMP,
                productosMp: productosMP,
                productosMi: productosMI,
            }).catch(e => console.error('Error guardando catálogo Stock de Fábrica:', e));
        }, 600);
        return () => clearTimeout(t);
    }, [loaded, categoriasMP, subcatsMP, productosMP, productosMI]);

    // ── Derived lists ────────────────────────────────────────────────────────
    const subcatsForCategoria = useMemo(
        () => subcatsMP.filter(s => s.categoria === mpCategoria).map(s => s.nombre),
        [subcatsMP, mpCategoria]
    );

    const productosForSubcat = useMemo(() => {
        if (!mpSubcat) return [];
        return productosMP.filter(p => p.categoria === mpCategoria && p.subcategoria === mpSubcat).map(p => p.nombre);
    }, [productosMP, mpCategoria, mpSubcat]);

    const freezerOptionsMp = mpUbicacion === 'FÁBRICA' ? FREEZERS_FABRICA : PALLETS_GABOTO;
    const freezerOptionsMi = miUbicacion === 'FÁBRICA' ? FREEZERS_FABRICA : PALLETS_GABOTO;
    const freezerOptionsModal = modalUbicacion === 'FÁBRICA' ? FREEZERS_FABRICA : PALLETS_GABOTO;

    const subcatsForModal = useMemo(
        () => subcatsMP.filter(s => s.categoria === modalCategoria).map(s => s.nombre),
        [subcatsMP, modalCategoria]
    );
    const productosForModal = useMemo(() => {
        if (!modalSubcat) return [];
        return productosMP.filter(p => p.categoria === modalCategoria && p.subcategoria === modalSubcat).map(p => p.nombre);
    }, [productosMP, modalCategoria, modalSubcat]);

    // Auto-select subcategory when there's only one (POLLO / CORDERO)
    useEffect(() => {
        if (subcatsForCategoria.length === 1) setMpSubcat(subcatsForCategoria[0]);
        else setMpSubcat('');
        setMpProducto('');
    }, [mpCategoria]); // eslint-disable-line react-hooks/exhaustive-deps

    const allMateriasPrimas = useMemo(
        () => productosMP.map(p => p.nombre).sort(),
        [productosMP]
    );

    const subcatsForCrudProd = useMemo(
        () => subcatsMP.filter(s => s.categoria === crudProdCat).map(s => s.nombre),
        [subcatsMP, crudProdCat]
    );

    // ── Handlers – Materia Prima ─────────────────────────────────────────────
    const handleAddMP = (e: React.FormEvent) => {
        e.preventDefault();
        if (!mpProducto || !mpUbicacion || !mpFreezer) return;

        const entry: StockMP = {
            id: `${Date.now()}-${Math.random()}`,
            categoria: mpCategoria,
            subcategoria: mpSubcat,
            producto: mpProducto,
            kg: (parseFloat(mpKg) || 0) * (mpTipo === 'salida' ? -1 : 1),
            unidades: (parseInt(mpUnidades) || 0) * (mpTipo === 'salida' ? -1 : 1),
            bolsas: parseInt(mpBolsas) || 0,
            ubicacion: mpUbicacion,
            freezer: mpFreezer,
            caja: mpUbicacion === 'FRIGORÍFICO GABOTO' ? (parseInt(mpCaja) || 0) : undefined,
            fecha: new Date().toISOString().split('T')[0],
            tipo: mpTipo,
            motivo: mpMotivo,
        };

        setStockMP(prev => [...prev, entry]);
        sfApi.addMP(entry).catch(err => console.error('Error guardando entrada MP:', err));
        setMpKg(''); setMpUnidades(''); setMpFreezer(''); setMpCaja(''); setMpBolsas(''); setMpMotivo('');
    };

    const handleEditMP = (entry: StockMP) => {
        setEditingId(entry.id);
        setModalCategoria(entry.categoria);
        setModalSubcat(entry.subcategoria);
        setModalProducto(entry.producto);
        setModalKg(entry.kg.toString());
        setModalUnidades(entry.unidades.toString());
        setModalUbicacion(entry.ubicacion);
        setModalFreezer(entry.freezer);
        setModalCaja(entry.caja?.toString() || '');
        setShowEditModal(true);
    };

    const handleSaveModal = (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingId || !modalProducto || !modalUbicacion || !modalFreezer) return;
        let actualizado: StockMP | null = null;
        setStockMP(prev => prev.map(item => {
            if (item.id !== editingId) return item;
            actualizado = {
                ...item,
                categoria: modalCategoria,
                subcategoria: modalSubcat,
                producto: modalProducto,
                kg: parseFloat(modalKg) || 0,
                unidades: parseInt(modalUnidades) || 0,
                ubicacion: modalUbicacion,
                freezer: modalFreezer,
                caja: modalUbicacion === 'FRIGORÍFICO GABOTO' ? (parseInt(modalCaja) || 0) : undefined,
            };
            return actualizado;
        }));
        // Editar en backend = borrar el movimiento y volver a insertarlo con el mismo id
        if (actualizado) {
            const upd = actualizado;
            sfApi.delMP(editingId)
                .then(() => sfApi.addMP(upd))
                .catch(err => console.error('Error editando movimiento MP:', err));
        }
        setShowEditModal(false);
        setEditingId(null);
    };

    const cancelEditMP = () => {
        setShowEditModal(false);
        setEditingId(null);
    };

    const handleDeleteMP = (id: string) => {
        setStockMP(prev => prev.filter(e => e.id !== id));
        sfApi.delMP(id).catch(err => console.error('Error borrando movimiento MP:', err));
    };

    // ── Handlers – Materia Intermedia ────────────────────────────────────────
    const handleAddMI = (e: React.FormEvent) => {
        e.preventDefault();
        if (!miProducto || !miOrigen || !miUbicacion || !miFreezer) return;

        const miEntry: StockMI = {
            id: `${Date.now()}-${Math.random()}`,
            producto: miProducto,
            kgMP: (parseFloat(miKgMP) || 0) * (miTipo === 'salida' ? -1 : 1),
            unidadesMP: (parseInt(miUnidadesMP) || 0) * (miTipo === 'salida' ? -1 : 1),
            kg: (parseFloat(miKg) || 0) * (miTipo === 'salida' ? -1 : 1),
            unidades: (parseInt(miUnidades) || 0) * (miTipo === 'salida' ? -1 : 1),
            ubicacion: miUbicacion,
            freezer: miFreezer,
            origenProducto: miOrigen,
            fecha: new Date().toISOString().split('T')[0],
            tipo: miTipo,
            motivo: miMotivo,
        };

        // Automáticamente generar una salida de Materia Prima si es una Entrada de Producción
        if (miTipo === 'entrada') {
            const mpProd = productosMP.find(p => p.nombre === miOrigen);
            if (mpProd) {
                const mpEntry: StockMP = {
                    id: `${Date.now()}-AUTO-MP-${Math.random()}`,
                    categoria: mpProd.categoria,
                    subcategoria: mpProd.subcategoria,
                    producto: mpProd.nombre,
                    kg: (parseFloat(miKgMP) || 0) * -1,
                    unidades: (parseInt(miUnidadesMP) || 0) * -1,
                    ubicacion: miUbicacion,
                    freezer: miFreezer,
                    fecha: new Date().toISOString().split('T')[0],
                    tipo: 'salida',
                    motivo: `PROCESAMIENTO: ${miProducto}`,
                };
                setStockMP(prev => [...prev, mpEntry]);
                sfApi.addMP(mpEntry).catch(err => console.error('Error guardando salida MP automática:', err));
            }
        }

        setStockMI(prev => [miEntry, ...prev]);
        sfApi.addMI(miEntry).catch(err => console.error('Error guardando movimiento MI:', err));
        setMiKgMP(''); setMiUnidadesMP(''); setMiKg(''); setMiUnidades(''); setMiFreezer(''); setMiOrigen(''); setMiMotivo('');
    };

    const handleDeleteMI = (id: string) => {
        setStockMI(prev => prev.filter(e => e.id !== id));
        sfApi.delMI(id).catch(err => console.error('Error borrando movimiento MI:', err));
    };

    // ── Handlers – CRUD Categorías MP ───────────────────────────────────────
    const handleAddCatMP = () => {
        const name = newCatName.trim().toUpperCase();
        if (!name || categoriasMP.some(c => c.nombre === name)) return;
        setCategoriasMP(prev => [...prev, { id: `${name}__${Date.now()}`, nombre: name }]);
        setNewCatName('');
    };
    const handleDeleteCatMP = (id: string) => {
        const cat = categoriasMP.find(c => c.id === id);
        if (!cat) return;
        setCategoriasMP(prev => prev.filter(c => c.id !== id));
        setSubcatsMP(prev => prev.filter(s => s.categoria !== cat.nombre));
        setProductosMP(prev => prev.filter(p => p.categoria !== cat.nombre));
    };
    const handleSaveCatMP = (id: string) => {
        const name = editCatName.trim().toUpperCase();
        if (!name) return;
        const old = categoriasMP.find(c => c.id === id);
        if (!old) return;
        setCategoriasMP(prev => prev.map(c => c.id === id ? { ...c, nombre: name } : c));
        setSubcatsMP(prev => prev.map(s => s.categoria === old.nombre ? { ...s, categoria: name } : s));
        setProductosMP(prev => prev.map(p => p.categoria === old.nombre ? { ...p, categoria: name } : p));
        setEditCatId(null);
        setEditCatName('');
    };

    // ── Handlers – CRUD Subcategorías MP ────────────────────────────────────
    const handleAddSubcatMP = () => {
        const name = newSubcatName.trim();
        if (!name) return;
        if (subcatsMP.some(s => s.categoria === crudCatFilter && s.nombre === name)) return;
        setSubcatsMP(prev => [...prev, { id: `${crudCatFilter}__${name}__${Date.now()}`, categoria: crudCatFilter, nombre: name }]);
        setNewSubcatName('');
    };
    const handleDeleteSubcatMP = (id: string) => {
        const sub = subcatsMP.find(s => s.id === id);
        if (!sub) return;
        setSubcatsMP(prev => prev.filter(s => s.id !== id));
        setProductosMP(prev => prev.filter(p => !(p.categoria === sub.categoria && p.subcategoria === sub.nombre)));
    };

    // ── Handlers – CRUD Productos MP ────────────────────────────────────────
    const handleAddProdMP = () => {
        const name = newProdMpName.trim();
        if (!name || !crudProdSubcat) return;
        if (productosMP.some(p => p.categoria === crudProdCat && p.subcategoria === crudProdSubcat && p.nombre === name)) return;
        setProductosMP(prev => [...prev, { id: `${crudProdCat}__${crudProdSubcat}__${name}__${Date.now()}`, categoria: crudProdCat, subcategoria: crudProdSubcat, nombre: name }]);
        setNewProdMpName('');
    };
    const handleDeleteProdMP = (id: string) => setProductosMP(prev => prev.filter(p => p.id !== id));
    const handleSaveProdMP = (id: string) => {
        const name = editProdMpName.trim();
        if (!name) return;
        setProductosMP(prev => prev.map(p => p.id === id ? { ...p, nombre: name } : p));
        setEditProdMpId(null);
        setEditProdMpName('');
    };

    // ── Handlers – CRUD Productos MI ────────────────────────────────────────
    const handleAddProdMI = () => {
        const name = newProdMiName.trim();
        if (!name) return;
        if (productosMI.some(p => p.nombre === name)) return;
        setProductosMI(prev => [...prev, { id: `${name}__${Date.now()}`, nombre: name }]);
        setNewProdMiName('');
    };
    const handleDeleteProdMI = (id: string) => setProductosMI(prev => prev.filter(p => p.id !== id));
    const handleSaveProdMI = (id: string) => {
        const name = editProdMiName.trim();
        if (!name) return;
        setProductosMI(prev => prev.map(p => p.id === id ? { ...p, nombre: name } : p));
        setEditProdMiId(null);
        setEditProdMiName('');
    };

    // ── Filtered and Sorted data ──────────────────────────────────────────────
    const sortedMP = useMemo(() => {
        const filtered = stockMP.filter(e => {
            if (filterCategoria && e.categoria !== filterCategoria) return false;
            if (filterUbicacion && e.ubicacion !== filterUbicacion) return false;
            return true;
        });

        if (!sortMP) return filtered;

        return [...filtered].sort((a, b) => {
            const aVal = a[sortMP.key] ?? '';
            const bVal = b[sortMP.key] ?? '';
            if (aVal < bVal) return sortMP.dir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortMP.dir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [stockMP, filterCategoria, filterUbicacion, sortMP]);

    const sortedMI = useMemo(() => {
        if (!sortMI) return stockMI;

        return [...stockMI].sort((a, b) => {
            const aVal = a[sortMI.key] ?? '';
            const bVal = b[sortMI.key] ?? '';
            if (aVal < bVal) return sortMI.dir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortMI.dir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [stockMI, sortMI]);

    const handleSortMP = (key: keyof StockMP) => {
        setSortMP(prev => {
            if (prev?.key === key) {
                return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            }
            return { key, dir: 'asc' };
        });
    };

    const handleSortMI = (key: keyof StockMI) => {
        setSortMI(prev => {
            if (prev?.key === key) {
                return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
            }
            return { key, dir: 'asc' };
        });
    };

    const getSortArrow = (current: { key: string; dir: 'asc' | 'desc' } | null, key: string) => {
        if (!current || current.key !== key) return ' ↕';
        return current.dir === 'asc' ? ' ▲' : ' ▼';
    };

    // ── Aggregated Reports ──────────────────────────────────────────────────
    const mpReport = useMemo(() => {
        const groups: Record<string, { categoria: string; subcategoria: string; producto: string; kg: number; unidades: number }> = {};
        stockMP.forEach(e => {
            const key = `${e.categoria}|${e.subcategoria}|${e.producto}`;
            if (!groups[key]) {
                groups[key] = { categoria: e.categoria, subcategoria: e.subcategoria, producto: e.producto, kg: 0, unidades: 0 };
            }
            groups[key].kg += e.kg;
            groups[key].unidades += e.unidades;
        });
        return Object.values(groups).sort((a, b) => a.producto.localeCompare(b.producto));
    }, [stockMP]);

    const miReport = useMemo(() => {
        const groups: Record<string, { producto: string; kg: number; unidades: number }> = {};
        stockMI.forEach(e => {
            const key = e.producto;
            if (!groups[key]) {
                groups[key] = { producto: e.producto, kg: 0, unidades: 0 };
            }
            groups[key].kg += e.kg;
            groups[key].unidades += e.unidades;
        });
        return Object.values(groups).sort((a, b) => a.producto.localeCompare(b.producto));
    }, [stockMI]);

    // ── Stock vivo por ubicación física (para Salida y para Stock de Cajas) ──
    // Cada "línea viva" agrupa todos los movimientos de un mismo producto en una
    // misma ubicación física (freezer o caja) y deja el neto kg/unidades/bolsas.
    interface LiveLineMP {
        key: string;            // identidad de la línea (ubicacion|freezer|caja|cat|sub|prod)
        groupKey: string;       // identidad del contenedor (ubicacion|freezer|caja)
        ubicacion: Ubicacion;
        freezer: string;
        caja?: number;
        categoria: Categoria;
        subcategoria: string;
        producto: string;
        kg: number;
        unidades: number;
        bolsas: number;
        fecha: string;          // fecha de ingreso más antigua
    }

    const liveLinesMP = useMemo<LiveLineMP[]>(() => {
        const map: Record<string, LiveLineMP> = {};
        stockMP.forEach(e => {
            const cajaPart = e.caja != null ? `|${e.caja}` : '';
            const groupKey = `${e.ubicacion}|${e.freezer}${cajaPart}`;
            const key = `${groupKey}|${e.categoria}|${e.subcategoria}|${e.producto}`;
            if (!map[key]) {
                map[key] = {
                    key, groupKey,
                    ubicacion: e.ubicacion, freezer: e.freezer, caja: e.caja,
                    categoria: e.categoria, subcategoria: e.subcategoria, producto: e.producto,
                    kg: 0, unidades: 0, bolsas: 0, fecha: e.fecha,
                };
            }
            map[key].kg += e.kg;
            map[key].unidades += e.unidades;
            // Las bolsas sólo cuentan en las entradas (las salidas no llevan bolsas explícitas)
            map[key].bolsas += (e.bolsas || 0) * ((e.kg < 0 || e.tipo === 'salida') ? -1 : 1);
            if (e.fecha < map[key].fecha) map[key].fecha = e.fecha;
        });
        // Sólo líneas con algo en stock
        return Object.values(map).filter(l => l.kg > 0.0001 || l.unidades > 0);
    }, [stockMP]);

    // Contenedores (freezers / cajas) con su contenido vivo
    interface LiveGroupMP {
        groupKey: string;
        ubicacion: Ubicacion;
        freezer: string;
        caja?: number;
        items: LiveLineMP[];
        totalKg: number;
        totalUnidades: number;
        totalBolsas: number;
        fecha: string;
    }
    const liveGroupsMP = useMemo<LiveGroupMP[]>(() => {
        const map: Record<string, LiveGroupMP> = {};
        liveLinesMP.forEach(l => {
            if (!map[l.groupKey]) {
                map[l.groupKey] = {
                    groupKey: l.groupKey, ubicacion: l.ubicacion, freezer: l.freezer, caja: l.caja,
                    items: [], totalKg: 0, totalUnidades: 0, totalBolsas: 0, fecha: l.fecha,
                };
            }
            const g = map[l.groupKey];
            g.items.push(l);
            g.totalKg += l.kg;
            g.totalUnidades += l.unidades;
            g.totalBolsas += Math.max(0, l.bolsas);
            if (l.fecha < g.fecha) g.fecha = l.fecha;
        });
        return Object.values(map).sort((a, b) => {
            if (a.ubicacion !== b.ubicacion) return a.ubicacion.localeCompare(b.ubicacion);
            if (a.freezer !== b.freezer) return a.freezer.localeCompare(b.freezer);
            return (a.caja || 0) - (b.caja || 0);
        });
    }, [liveLinesMP]);

    // Sólo cajas de Gaboto (para el tab "Stock de Cajas")
    const liveCajas = useMemo(
        () => liveGroupsMP.filter(g => g.ubicacion === 'FRIGORÍFICO GABOTO' && g.caja != null),
        [liveGroupsMP]
    );

    // Stock vivo de Materia Intermedia por contenedor
    interface LiveLineMI {
        key: string; groupKey: string;
        ubicacion: Ubicacion; freezer: string;
        producto: string; kg: number; unidades: number;
    }
    const liveLinesMI = useMemo<LiveLineMI[]>(() => {
        const map: Record<string, LiveLineMI> = {};
        stockMI.forEach(e => {
            const groupKey = `${e.ubicacion}|${e.freezer}`;
            const key = `${groupKey}|${e.producto}`;
            if (!map[key]) {
                map[key] = { key, groupKey, ubicacion: e.ubicacion, freezer: e.freezer, producto: e.producto, kg: 0, unidades: 0 };
            }
            map[key].kg += e.kg;
            map[key].unidades += e.unidades;
        });
        return Object.values(map).filter(l => l.kg > 0.0001 || l.unidades > 0);
    }, [stockMI]);

    interface LiveGroupMI {
        groupKey: string; ubicacion: Ubicacion; freezer: string;
        items: LiveLineMI[]; totalKg: number; totalUnidades: number;
    }
    const liveGroupsMI = useMemo<LiveGroupMI[]>(() => {
        const map: Record<string, LiveGroupMI> = {};
        liveLinesMI.forEach(l => {
            if (!map[l.groupKey]) {
                map[l.groupKey] = { groupKey: l.groupKey, ubicacion: l.ubicacion, freezer: l.freezer, items: [], totalKg: 0, totalUnidades: 0 };
            }
            map[l.groupKey].items.push(l);
            map[l.groupKey].totalKg += l.kg;
            map[l.groupKey].totalUnidades += l.unidades;
        });
        return Object.values(map).sort((a, b) => {
            if (a.ubicacion !== b.ubicacion) return a.ubicacion.localeCompare(b.ubicacion);
            return a.freezer.localeCompare(b.freezer);
        });
    }, [liveLinesMI]);

    const selectedGroupMP = useMemo(
        () => liveGroupsMP.find(g => g.groupKey === salidaGroupMP) || null,
        [liveGroupsMP, salidaGroupMP]
    );
    const selectedGroupMI = useMemo(
        () => liveGroupsMI.find(g => g.groupKey === salidaGroupMI) || null,
        [liveGroupsMI, salidaGroupMI]
    );

    const fmtContenedor = (g: { ubicacion: Ubicacion; freezer: string; caja?: number }) =>
        g.caja != null ? `${g.freezer} · Caja ${g.caja}` : g.freezer;

    // ── Registrar salida por selección (MP) ──────────────────────────────────
    // Genera movimientos de salida (kg/unidades/bolsas negativos) para los items elegidos.
    const registrarSalidaMP = (mode: 'todo' | 'parcial') => {
        if (!selectedGroupMP) return;
        const fecha = new Date().toISOString().split('T')[0];
        const nuevos: StockMP[] = [];
        selectedGroupMP.items.forEach(item => {
            let kg: number, unidades: number, bolsas: number;
            if (mode === 'todo') {
                kg = item.kg; unidades = item.unidades; bolsas = Math.max(0, item.bolsas);
            } else {
                const q = salidaQtyMP[item.key];
                if (!q) return;
                kg = Math.min(parseFloat(q.kg) || 0, item.kg);
                unidades = Math.min(parseInt(q.unidades) || 0, item.unidades);
                bolsas = Math.min(parseInt(q.bolsas) || 0, Math.max(0, item.bolsas));
                if (kg <= 0 && unidades <= 0 && bolsas <= 0) return;
            }
            nuevos.push({
                id: `${Date.now()}-SAL-${Math.random()}`,
                categoria: item.categoria, subcategoria: item.subcategoria, producto: item.producto,
                kg: -kg, unidades: -unidades, bolsas: -bolsas,
                ubicacion: item.ubicacion, freezer: item.freezer, caja: item.caja,
                fecha, tipo: 'salida',
                motivo: salidaMotivoMP || (mode === 'todo' ? 'Vaciado de contenedor' : 'Salida'),
            });
        });
        if (nuevos.length === 0) return;
        setStockMP(prev => [...prev, ...nuevos]);
        sfApi.addMP(nuevos).catch(err => console.error('Error guardando salida MP:', err));
        setSalidaGroupMP(null);
        setSalidaQtyMP({});
        setSalidaMotivoMP('');
    };

    // ── Registrar salida por selección (MI) ──────────────────────────────────
    const registrarSalidaMI = (mode: 'todo' | 'parcial') => {
        if (!selectedGroupMI) return;
        const fecha = new Date().toISOString().split('T')[0];
        const nuevos: StockMI[] = [];
        selectedGroupMI.items.forEach(item => {
            let kg: number, unidades: number;
            if (mode === 'todo') {
                kg = item.kg; unidades = item.unidades;
            } else {
                const q = salidaQtyMI[item.key];
                if (!q) return;
                kg = Math.min(parseFloat(q.kg) || 0, item.kg);
                unidades = Math.min(parseInt(q.unidades) || 0, item.unidades);
                if (kg <= 0 && unidades <= 0) return;
            }
            nuevos.push({
                id: `${Date.now()}-SAL-${Math.random()}`,
                producto: item.producto,
                kgMP: 0, unidadesMP: 0,
                kg: -kg, unidades: -unidades,
                ubicacion: item.ubicacion, freezer: item.freezer,
                origenProducto: item.producto,
                fecha, tipo: 'salida',
                motivo: salidaMotivoMI || (mode === 'todo' ? 'Vaciado de contenedor' : 'Salida'),
            });
        });
        if (nuevos.length === 0) return;
        setStockMI(prev => [...nuevos, ...prev]);
        sfApi.addMI(nuevos).catch(err => console.error('Error guardando salida MI:', err));
        setSalidaGroupMI(null);
        setSalidaQtyMI({});
        setSalidaMotivoMI('');
    };

    // ── Category color helpers ────────────────────────────────────────────────
    const catColor = (cat: Categoria) => (({
        NOVILLO: { bg: '#fff3e0', fg: '#e65100' },
        POLLO: { bg: '#e3f2fd', fg: '#0277bd' },
        CORDERO: { bg: '#f3e5f5', fg: '#6a1b9a' },
    } as Record<string, { bg: string; fg: string }>)[cat] ?? { bg: 'rgba(255,255,255,0.12)', fg: '#f8f9fa' });

    // ── Render: Selector de salida sobre stock real (MP) ─────────────────────
    const renderSalidaSelectorMP = () => {
        // Paso 1: elegir contenedor (freezer o caja)
        if (!selectedGroupMP) {
            return (
                <div>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: '4px 0 16px' }}>
                        Elegí un freezer o una caja para ver lo que tiene adentro y sacar mercadería.
                    </p>
                    {liveGroupsMP.length === 0 ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                            No hay stock disponible para sacar.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                            {liveGroupsMP.map(g => (
                                <button
                                    key={g.groupKey}
                                    onClick={() => { setSalidaGroupMP(g.groupKey); setSalidaQtyMP({}); setSalidaMotivoMP(''); }}
                                    style={{
                                        textAlign: 'left', cursor: 'pointer', padding: '14px 16px', borderRadius: '8px',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        background: g.ubicacion === 'FÁBRICA' ? 'rgba(46,125,50,0.18)' : 'rgba(245,127,23,0.15)',
                                        color: '#f8f9fa', transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#f39c12')}
                                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                                >
                                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#f39c12', marginBottom: '4px' }}>
                                        {fmtContenedor(g)}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>{g.ubicacion}</div>
                                    <div style={{ fontSize: '0.82rem' }}>
                                        <strong>{g.items.length}</strong> producto(s) · <strong>{g.totalKg.toFixed(2)}</strong> kg
                                        {g.totalBolsas > 0 && <> · <strong>{g.totalBolsas}</strong> bolsas</>}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // Paso 2: ver contenido del contenedor y sacar
        const g = selectedGroupMP;
        return (
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                        <button onClick={() => setSalidaGroupMP(null)} style={{ background: 'none', border: 'none', color: '#f39c12', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>
                            ← Volver
                        </button>
                        <h4 style={{ margin: '6px 0 0', color: '#f39c12', fontSize: '1.05rem' }}>{fmtContenedor(g)}</h4>
                        <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>{g.ubicacion}</span>
                    </div>
                    <button
                        onClick={() => { if (window.confirm(`¿Vaciar por completo ${fmtContenedor(g)}? Se sacará todo lo que tiene adentro.`)) registrarSalidaMP('todo'); }}
                        style={{ padding: '10px 20px', backgroundColor: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        🗑️ Vaciar caja completa
                    </button>
                </div>

                <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Producto / Corte</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>En stock</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Sacar Kg</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Sacar Unid.</th>
                                {g.totalBolsas > 0 && <th style={{ ...thStyle, textAlign: 'center' }}>Sacar Bolsas</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {g.items.map((item, idx) => {
                                const q = salidaQtyMP[item.key] || { kg: '', unidades: '', bolsas: '' };
                                return (
                                    <tr key={item.key} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                        <td style={{ ...tdStyle, fontWeight: 500 }}>
                                            {item.producto}
                                            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>{item.categoria} · {item.subcategoria}</div>
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center', color: '#a5d6a7' }}>
                                            <strong>{item.kg.toFixed(2)}</strong> kg · {item.unidades} u.
                                            {item.bolsas > 0 && <> · {item.bolsas} bls</>}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input type="number" min="0" max={item.kg} step="0.01" value={q.kg}
                                                placeholder={item.kg.toFixed(2)}
                                                onChange={e => setSalidaQtyMP(prev => ({ ...prev, [item.key]: { ...q, kg: e.target.value } }))}
                                                style={{ ...inputStyle, width: '90px', textAlign: 'center' }} />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input type="number" min="0" max={item.unidades} value={q.unidades}
                                                placeholder={String(item.unidades)}
                                                onChange={e => setSalidaQtyMP(prev => ({ ...prev, [item.key]: { ...q, unidades: e.target.value } }))}
                                                style={{ ...inputStyle, width: '80px', textAlign: 'center' }} />
                                        </td>
                                        {g.totalBolsas > 0 && (
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <input type="number" min="0" max={Math.max(0, item.bolsas)} value={q.bolsas}
                                                    placeholder={String(Math.max(0, item.bolsas))}
                                                    onChange={e => setSalidaQtyMP(prev => ({ ...prev, [item.key]: { ...q, bolsas: e.target.value } }))}
                                                    style={{ ...inputStyle, width: '80px', textAlign: 'center' }} />
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginTop: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 240px' }}>
                        <label style={labelStyle}>Motivo de la salida (opcional)</label>
                        <input type="text" value={salidaMotivoMP} onChange={e => setSalidaMotivoMP(e.target.value)} placeholder="Ej: Venta, lo llevó un camioneto, procesamiento..." style={inputStyle} />
                    </div>
                    <button
                        onClick={() => registrarSalidaMP('parcial')}
                        style={{ padding: '10px 30px', backgroundColor: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        Registrar salida seleccionada
                    </button>
                </div>
            </div>
        );
    };

    // ── Render: Selector de salida sobre stock real (MI) ─────────────────────
    const renderSalidaSelectorMI = () => {
        if (!selectedGroupMI) {
            return (
                <div>
                    <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: '4px 0 16px' }}>
                        Elegí un freezer / pallet para ver el producto intermedio que tiene y sacar.
                    </p>
                    {liveGroupsMI.length === 0 ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                            No hay stock disponible para sacar.
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
                            {liveGroupsMI.map(g => (
                                <button
                                    key={g.groupKey}
                                    onClick={() => { setSalidaGroupMI(g.groupKey); setSalidaQtyMI({}); setSalidaMotivoMI(''); }}
                                    style={{
                                        textAlign: 'left', cursor: 'pointer', padding: '14px 16px', borderRadius: '8px',
                                        border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(52,152,219,0.15)',
                                        color: '#f8f9fa', transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#3498db')}
                                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)')}
                                >
                                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#3498db', marginBottom: '4px' }}>{g.freezer}</div>
                                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' }}>{g.ubicacion}</div>
                                    <div style={{ fontSize: '0.82rem' }}><strong>{g.items.length}</strong> producto(s) · <strong>{g.totalKg.toFixed(2)}</strong> kg</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        const g = selectedGroupMI;
        return (
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                        <button onClick={() => setSalidaGroupMI(null)} style={{ background: 'none', border: 'none', color: '#3498db', cursor: 'pointer', fontSize: '0.85rem', padding: 0 }}>
                            ← Volver
                        </button>
                        <h4 style={{ margin: '6px 0 0', color: '#3498db', fontSize: '1.05rem' }}>{g.freezer}</h4>
                        <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>{g.ubicacion}</span>
                    </div>
                    <button
                        onClick={() => { if (window.confirm(`¿Vaciar por completo ${g.freezer}?`)) registrarSalidaMI('todo'); }}
                        style={{ padding: '10px 20px', backgroundColor: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        🗑️ Vaciar completo
                    </button>
                </div>

                <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Producto Intermedio</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>En stock</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Sacar Kg</th>
                                <th style={{ ...thStyle, textAlign: 'center' }}>Sacar Unid.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {g.items.map((item, idx) => {
                                const q = salidaQtyMI[item.key] || { kg: '', unidades: '' };
                                return (
                                    <tr key={item.key} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                        <td style={{ ...tdStyle, fontWeight: 500 }}>{item.producto}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center', color: '#81c784' }}><strong>{item.kg.toFixed(2)}</strong> kg · {item.unidades} u.</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input type="number" min="0" max={item.kg} step="0.01" value={q.kg}
                                                placeholder={item.kg.toFixed(2)}
                                                onChange={e => setSalidaQtyMI(prev => ({ ...prev, [item.key]: { ...q, kg: e.target.value } }))}
                                                style={{ ...inputStyle, width: '90px', textAlign: 'center' }} />
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input type="number" min="0" max={item.unidades} value={q.unidades}
                                                placeholder={String(item.unidades)}
                                                onChange={e => setSalidaQtyMI(prev => ({ ...prev, [item.key]: { ...q, unidades: e.target.value } }))}
                                                style={{ ...inputStyle, width: '80px', textAlign: 'center' }} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginTop: '16px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 240px' }}>
                        <label style={labelStyle}>Motivo de la salida (opcional)</label>
                        <input type="text" value={salidaMotivoMI} onChange={e => setSalidaMotivoMI(e.target.value)} placeholder="Ej: Venta, descarte..." style={inputStyle} />
                    </div>
                    <button
                        onClick={() => registrarSalidaMI('parcial')}
                        style={{ padding: '10px 30px', backgroundColor: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                    >
                        Registrar salida seleccionada
                    </button>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div style={{ color: '#f8f9fa' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0', marginBottom: '24px', borderBottom: '2px solid rgba(255,255,255,0.12)' }}>
                {[
                    { key: 'mp', label: 'Stock de Materia Prima' },
                    { key: 'mi', label: 'Stock de Materia Intermedia' },
                    { key: 'crud', label: 'Gestión de Productos' },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setView(tab.key as 'mp' | 'mi' | 'crud')}
                        style={{
                            padding: '12px 24px',
                            border: 'none',
                            borderBottom: view === tab.key ? '3px solid #f39c12' : '3px solid transparent',
                            backgroundColor: 'transparent',
                            fontWeight: 'bold',
                            fontSize: '0.95rem',
                            color: view === tab.key ? '#f39c12' : 'rgba(255,255,255,0.45)',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ══════════════════════ MATERIA PRIMA ══════════════════════════ */}
            {view === 'mp' && (
                <div>
                    {/* Sub-tabs MP */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        {[
                            { key: 'form', label: 'Registrar Movimiento' },
                            { key: 'resumen', label: 'Resumen de Stock' },
                            { key: 'cajas', label: 'Stock de Cajas' },
                            { key: 'historial', label: 'Historial / Registros' },
                        ].map(sub => (
                            <button
                                key={sub.key}
                                onClick={() => setMpTab(sub.key as any)}
                                style={{
                                    padding: '8px 18px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                                    backgroundColor: mpTab === sub.key ? '#f39c12' : 'rgba(255,255,255,0.1)',
                                    color: mpTab === sub.key ? '#000' : 'rgba(255,255,255,0.7)',
                                    fontWeight: 'bold', fontSize: '0.85rem', transition: 'all 0.15s'
                                }}
                            >
                                {sub.label}
                            </button>
                        ))}
                    </div>

                    {/* ── MP: Form ── */}
                    {mpTab === 'form' && (
                        <div style={{
                            background: 'rgba(0,0,0,0.25)',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h4 style={{ margin: 0, color: '#f39c12', fontSize: '1rem' }}>
                                    {mpTipo === 'entrada' ? '+ Cargar Entrada de Stock' : '- Registrar Salida de Stock'}
                                </h4>
                                <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '3px' }}>
                                    {(['entrada', 'salida'] as const).map(t => (
                                        <button
                                            key={t}
                                            onClick={() => setMpTipo(t)}
                                            style={{
                                                padding: '5px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
                                                backgroundColor: mpTipo === t ? (t === 'entrada' ? '#2e7d32' : '#c62828') : 'transparent',
                                                color: mpTipo === t ? '#fff' : 'rgba(255,255,255,0.4)',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {t === 'entrada' ? 'ENTRADA' : 'SALIDA'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {mpTipo === 'entrada' && (
                            <form onSubmit={handleAddMP}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'end' }}>
                                    <div>
                                        <label style={labelStyle}>Categoría</label>
                                        <select value={mpCategoria} onChange={e => setMpCategoria(e.target.value as Categoria)} style={selectStyle} required>
                                            {categoriasMP.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Subcategoría</label>
                                        <select value={mpSubcat} onChange={e => { setMpSubcat(e.target.value); setMpProducto(''); }} style={selectStyle} required>
                                            <option value="">-- Seleccionar --</option>
                                            {subcatsForCategoria.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Producto / Corte</label>
                                        <CustomSelect
                                            value={mpProducto}
                                            onChange={setMpProducto}
                                            options={productosForSubcat}
                                            placeholder="-- Seleccionar --"
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={labelStyle}>Kg</label>
                                            <input type="number" value={mpKg} onChange={e => setMpKg(e.target.value)} min="0" step="0.01" placeholder="0.00" style={inputStyle} required />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={labelStyle}>Unidades</label>
                                            <input type="number" value={mpUnidades} onChange={e => setMpUnidades(e.target.value)} min="0" placeholder="0" style={inputStyle} />
                                        </div>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Ubicación</label>
                                        <select value={mpUbicacion} onChange={e => { setMpUbicacion(e.target.value as Ubicacion); setMpFreezer(''); }} style={selectStyle} required>
                                            <option value="FÁBRICA">FÁBRICA</option>
                                            <option value="FRIGORÍFICO GABOTO">FRIGORÍFICO GABOTO</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>{mpUbicacion === 'FÁBRICA' ? 'Freezer' : 'Pallet'}</label>
                                        <select value={mpFreezer} onChange={e => setMpFreezer(e.target.value)} style={selectStyle} required>
                                            <option value="">-- Seleccionar --</option>
                                            {freezerOptionsMp.map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                    </div>
                                    {mpUbicacion === 'FRIGORÍFICO GABOTO' && (
                                        <>
                                            <div>
                                                <label style={labelStyle}>Caja (1-100)</label>
                                                <select value={mpCaja} onChange={e => setMpCaja(e.target.value)} style={selectStyle} required>
                                                    <option value="">-- Seleccionar --</option>
                                                    {[...Array(100)].map((_, i) => (
                                                        <option key={i + 1} value={i + 1}>Caja {i + 1}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label style={labelStyle}>Bolsas</label>
                                                <input type="number" value={mpBolsas} onChange={e => setMpBolsas(e.target.value)} min="0" placeholder="0" style={inputStyle} />
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <button type="submit" style={{ padding: '10px 50px', backgroundColor: '#f39c12', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                                        Cargar Entrada
                                    </button>
                                </div>
                            </form>
                            )}

                            {/* ── Salida por selección sobre stock real (MP) ── */}
                            {mpTipo === 'salida' && renderSalidaSelectorMP()}
                        </div>
                    )}

                    {/* ── MP: Resumen ── */}
                    {mpTab === 'resumen' && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                <h4 style={{ margin: 0, color: '#f39c12' }}>Stock Actual Consolidado</h4>
                                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>Total: {mpReport.length} productos</span>
                            </div>
                            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Categoría</th>
                                            <th style={thStyle}>Subcategoría</th>
                                            <th style={thStyle}>Producto / Corte</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Total Kg</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Total Unidades</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {mpReport.length === 0 ? (
                                            <tr><td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No hay stock registrado.</td></tr>
                                        ) : (
                                            mpReport.map((item, idx) => (
                                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                                    <td style={tdStyle}>{item.categoria}</td>
                                                    <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.5)' }}>{item.subcategoria}</td>
                                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{item.producto}</td>
                                                    <td style={{ ...tdStyle, textAlign: 'center', color: item.kg <= 0 ? '#ff5252' : '#a5d6a7' }}>
                                                        <strong>{item.kg.toFixed(2)}</strong> kg
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>{item.unidades} u.</td>
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                        <span style={{ fontSize: '1.2rem' }}>{item.kg > 50 ? '✅' : (item.kg > 0 ? '⚠️' : '❌')}</span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── MP: Stock de Cajas (Frigorífico Gaboto) ── */}
                    {mpTab === 'cajas' && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                                <h4 style={{ margin: 0, color: '#f39c12' }}>Stock de Cajas — Frigorífico Gaboto</h4>
                                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>
                                    {liveCajas.length} cajas · {liveCajas.reduce((s, c) => s + c.totalKg, 0).toFixed(2)} kg · {liveCajas.reduce((s, c) => s + c.totalBolsas, 0)} bolsas
                                </span>
                            </div>
                            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Número de caja</th>
                                            <th style={thStyle}>Fecha ingreso</th>
                                            <th style={thStyle}>Corte</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Bolsas</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Kgs</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {liveCajas.length === 0 ? (
                                            <tr><td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No hay cajas con stock en Gaboto.</td></tr>
                                        ) : (
                                            liveCajas.map((caja, cidx) => (
                                                <React.Fragment key={caja.groupKey}>
                                                    {caja.items.map((item, iidx) => (
                                                        <tr key={item.key} style={{ backgroundColor: cidx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                                            {iidx === 0 && (
                                                                <td rowSpan={caja.items.length} style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.1rem', color: '#f39c12', borderRight: '1px solid rgba(255,255,255,0.07)', verticalAlign: 'middle' }}>
                                                                    {caja.caja}
                                                                    <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>{caja.freezer}</div>
                                                                </td>
                                                            )}
                                                            <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem' }}>{item.fecha}</td>
                                                            <td style={{ ...tdStyle, fontWeight: 500 }}>
                                                                {item.producto}
                                                                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>{item.categoria} · {item.subcategoria}</div>
                                                            </td>
                                                            <td style={{ ...tdStyle, textAlign: 'center' }}>{Math.max(0, item.bolsas)}</td>
                                                            <td style={{ ...tdStyle, textAlign: 'center', color: '#a5d6a7', fontWeight: 600 }}>{item.kg.toFixed(2)}</td>
                                                            {iidx === 0 && (
                                                                <td rowSpan={caja.items.length} style={{ ...tdStyle, textAlign: 'center', verticalAlign: 'middle' }}>
                                                                    <button
                                                                        onClick={() => { setMpTab('form'); setMpTipo('salida'); setSalidaGroupMP(caja.groupKey); setSalidaQtyMP({}); setSalidaMotivoMP(''); }}
                                                                        title="Sacar / vaciar esta caja"
                                                                        style={{ padding: '6px 12px', backgroundColor: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem', whiteSpace: 'nowrap' }}
                                                                    >
                                                                        Sacar / Vaciar
                                                                    </button>
                                                                </td>
                                                            )}
                                                        </tr>
                                                    ))}
                                                    <tr style={{ backgroundColor: 'rgba(243,156,18,0.07)' }}>
                                                        <td colSpan={3} style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>Total caja {caja.caja}:</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700 }}>{caja.totalBolsas}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#f39c12' }}>{caja.totalKg.toFixed(2)}</td>
                                                        <td style={tdStyle}></td>
                                                    </tr>
                                                </React.Fragment>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── MP: Historial ── */}
                    {mpTab === 'historial' && (
                        <div>
                            {/* Filters */}
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
                                <select value={filterCategoria} onChange={e => setFilterCategoria(e.target.value)} style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '0.85rem', backgroundColor: '#f8f9fa', color: '#000' }}>
                                    <option value="">Todas las categorías</option>
                                    {categoriasMP.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                </select>
                                <select value={filterUbicacion} onChange={e => setFilterUbicacion(e.target.value)} style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '0.85rem', backgroundColor: '#f8f9fa', color: '#000' }}>
                                    <option value="">Todas las ubicaciones</option>
                                    <option value="FÁBRICA">FÁBRICA</option>
                                    <option value="FRIGORÍFICO GABOTO">FRIGORÍFICO GABOTO</option>
                                </select>
                                <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>{sortedMP.length} registros</span>
                            </div>

                            {/* Table */}
                            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => handleSortMP('categoria')} style={{ ...thStyle, cursor: 'pointer' }}>Categoría{getSortArrow(sortMP, 'categoria')}</th>
                                            <th onClick={() => handleSortMP('subcategoria')} style={{ ...thStyle, cursor: 'pointer' }}>Subcategoría{getSortArrow(sortMP, 'subcategoria')}</th>
                                            <th onClick={() => handleSortMP('producto')} style={{ ...thStyle, cursor: 'pointer' }}>Producto / Corte{getSortArrow(sortMP, 'producto')}</th>
                                            <th onClick={() => handleSortMP('kg')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Kg{getSortArrow(sortMP, 'kg')}</th>
                                            <th onClick={() => handleSortMP('unidades')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Unidades{getSortArrow(sortMP, 'unidades')}</th>
                                            <th onClick={() => handleSortMP('ubicacion')} style={{ ...thStyle, cursor: 'pointer' }}>Ubicación{getSortArrow(sortMP, 'ubicacion')}</th>
                                            <th onClick={() => handleSortMP('freezer')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Freezer / Pallet{getSortArrow(sortMP, 'freezer')}</th>
                                            <th onClick={() => handleSortMP('fecha')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Fecha{getSortArrow(sortMP, 'fecha')}</th>
                                            <th style={thStyle}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedMP.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', backgroundColor: '#48111d' }}>
                                                    Sin registros.
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedMP.map((entry, idx) => {
                                                const cc = catColor(entry.categoria);
                                                const isSalida = entry.tipo === 'salida';
                                                return (
                                                    <tr key={entry.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}
                                                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)')}
                                                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)')}
                                                    >
                                                        <td style={tdStyle}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 700, backgroundColor: cc.bg, color: cc.fg }}>
                                                                    {entry.categoria}
                                                                </span>
                                                                {isSalida && <span style={{ fontSize: '0.65rem', background: '#c62828', color: '#fff', padding: '1px 5px', borderRadius: '4px' }}>SALIDA</span>}
                                                            </div>
                                                        </td>
                                                        <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.55)' }}>{entry.subcategoria}</td>
                                                        <td style={{ ...tdStyle, fontWeight: 500 }}>
                                                            {entry.producto}
                                                            {isSalida && entry.motivo && <div style={{ fontSize: '0.7rem', color: '#ff8a80', fontStyle: 'italic' }}>📌 {entry.motivo}</div>}
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: isSalida ? '#ffab91' : 'inherit' }}>
                                                            <strong>{entry.kg}</strong> kg
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: isSalida ? '#ffab91' : 'inherit' }}>{entry.unidades} u.</td>
                                                        <td style={tdStyle}>
                                                            <span style={{
                                                                padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                                                                backgroundColor: entry.ubicacion === 'FÁBRICA' ? 'rgba(46,125,50,0.35)' : 'rgba(245,127,23,0.3)',
                                                                color: entry.ubicacion === 'FÁBRICA' ? '#a5d6a7' : '#ffcc80',
                                                            }}>
                                                                {entry.ubicacion}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#f39c12' }}>
                                                            {entry.freezer} {entry.caja ? `(Caja ${entry.caja})` : ''}
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                                                            {entry.fecha}
                                                        </td>
                                                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                            <button
                                                                onClick={() => handleEditMP(entry)}
                                                                title="Editar"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '1rem' }}
                                                            >✏️</button>
                                                            <button
                                                                onClick={() => handleDeleteMP(entry.id)}
                                                                title="Eliminar"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '1rem' }}
                                                            >🗑️</button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ═════════════════════ MATERIA INTERMEDIA ═══════════════════════ */}
            {view === 'mi' && (
                <div>
                    {/* Sub-tabs MI */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                        {[
                            { key: 'form', label: 'Registrar Movimiento' },
                            { key: 'resumen', label: 'Resumen de Stock' },
                            { key: 'historial', label: 'Historial / Registros' },
                        ].map(sub => (
                            <button
                                key={sub.key}
                                onClick={() => setMiTab(sub.key as any)}
                                style={{
                                    padding: '8px 18px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                                    backgroundColor: miTab === sub.key ? '#3498db' : 'rgba(255,255,255,0.1)',
                                    color: miTab === sub.key ? '#fff' : 'rgba(255,255,255,0.7)',
                                    fontWeight: 'bold', fontSize: '0.85rem', transition: 'all 0.15s'
                                }}
                            >
                                {sub.label}
                            </button>
                        ))}
                    </div>

                    {/* ── MI: Form ── */}
                    {miTab === 'form' && (
                        <div style={{
                            background: 'rgba(0,0,0,0.25)',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h4 style={{ margin: 0, color: '#3498db', fontSize: '1rem' }}>
                                    {miTipo === 'entrada' ? '+ Cargar Producción (Entrada)' : '- Registrar Salida de Stock'}
                                </h4>
                                <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '3px' }}>
                                    {(['entrada', 'salida'] as const).map(t => (
                                        <button
                                            key={t}
                                            onClick={() => setMiTipo(t)}
                                            style={{
                                                padding: '5px 15px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700,
                                                backgroundColor: miTipo === t ? (t === 'entrada' ? '#1976d2' : '#c62828') : 'transparent',
                                                color: miTipo === t ? '#fff' : 'rgba(255,255,255,0.4)',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {t === 'entrada' ? 'ENTRADA' : 'SALIDA'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {miTipo === 'entrada' && (
                            <form onSubmit={handleAddMI}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'end' }}>
                                    <div>
                                        <label style={labelStyle}>Producto Intermedio</label>
                                        <CustomSelect
                                            value={miProducto}
                                            onChange={setMiProducto}
                                            options={productosMI.map(p => p.nombre)}
                                            placeholder="-- Seleccionar --"
                                        />
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Materia Prima de Origen</label>
                                        <CustomSelect
                                            value={miOrigen}
                                            onChange={setMiOrigen}
                                            options={allMateriasPrimas}
                                            placeholder="-- Seleccionar --"
                                        />
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', borderLeft: '2px solid rgba(255,200,0,0.2)', paddingLeft: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ ...labelStyle, color: '#fbc02d' }}>Kg MP</label>
                                            <input type="number" value={miKgMP} onChange={e => setMiKgMP(e.target.value)} min="0" step="0.01" placeholder="0.00" style={inputStyle} required />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ ...labelStyle, color: '#fbc02d' }}>Ud. MP</label>
                                            <input type="number" value={miUnidadesMP} onChange={e => setMiUnidadesMP(e.target.value)} min="0" placeholder="0" style={inputStyle} />
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', borderLeft: '2px solid rgba(100,200,100,0.2)', paddingLeft: '8px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ ...labelStyle, color: '#81c784' }}>Kg Resultante</label>
                                            <input type="number" value={miKg} onChange={e => setMiKg(e.target.value)} min="0" step="0.01" placeholder="0.00" style={inputStyle} required />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ ...labelStyle, color: '#81c784' }}>Ud. Resultante</label>
                                            <input type="number" value={miUnidades} onChange={e => setMiUnidades(e.target.value)} min="0" placeholder="0" style={inputStyle} />
                                        </div>
                                    </div>

                                    <div>
                                        <label style={labelStyle}>Ubicación</label>
                                        <select value={miUbicacion} onChange={e => setMiUbicacion(e.target.value as Ubicacion)} style={selectStyle} required>
                                            <option value="FÁBRICA">FÁBRICA</option>
                                            <option value="FRIGORÍFICO GABOTO">FRIGORÍFICO GABOTO</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Freezer / Pallet</label>
                                        <select value={miFreezer} onChange={e => setMiFreezer(e.target.value)} style={selectStyle} required>
                                            <option value="">-- Seleccionar --</option>
                                            {freezerOptionsMi.map(f => <option key={f} value={f}>{f}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <button type="submit" style={{ padding: '10px 50px', backgroundColor: '#3498db', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                                        Cargar Producción
                                    </button>
                                </div>
                            </form>
                            )}

                            {/* ── Salida por selección sobre stock real (MI) ── */}
                            {miTipo === 'salida' && renderSalidaSelectorMI()}
                        </div>
                    )}

                    {/* ── MI: Resumen ── */}
                    {miTab === 'resumen' && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                <h4 style={{ margin: 0, color: '#3498db' }}>Resumen de Materia Intermedia</h4>
                                <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)' }}>Total: {miReport.length} productos</span>
                            </div>
                            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Producto Intermedio</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Total Kg</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Total Unidades</th>
                                            <th style={{ ...thStyle, textAlign: 'center' }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {miReport.length === 0 ? (
                                            <tr><td colSpan={4} style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No hay stock registrado.</td></tr>
                                        ) : (
                                            miReport.map((item, idx) => (
                                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{item.producto}</td>
                                                    <td style={{ ...tdStyle, textAlign: 'center', color: item.kg <= 0 ? '#ff5252' : '#81c784' }}>
                                                        <strong>{item.kg.toFixed(2)}</strong> kg
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>{item.unidades} u.</td>
                                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                        <span style={{ fontSize: '1.2rem' }}>{item.kg > 20 ? '✅' : (item.kg > 0 ? '⚠️' : '❌')}</span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── MI: Historial ── */}
                    {miTab === 'historial' && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                                <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>{sortedMI.length} registros</span>
                            </div>
                            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr>
                                            <th onClick={() => handleSortMI('producto')} style={{ ...thStyle, cursor: 'pointer' }}>Producto Intermedio{getSortArrow(sortMI, 'producto')}</th>
                                            <th onClick={() => handleSortMI('origenProducto')} style={{ ...thStyle, cursor: 'pointer' }}>Proviene de (MP){getSortArrow(sortMI, 'origenProducto')}</th>
                                            <th onClick={() => handleSortMI('kgMP')} style={{ ...thStyle, textAlign: 'center', borderLeft: '2px solid rgba(255,200,0,0.3)', cursor: 'pointer' }}>Kg MP{getSortArrow(sortMI, 'kgMP')}</th>
                                            <th onClick={() => handleSortMI('unidadesMP')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Ud. MP{getSortArrow(sortMI, 'unidadesMP')}</th>
                                            <th onClick={() => handleSortMI('kg')} style={{ ...thStyle, textAlign: 'center', borderLeft: '2px solid rgba(100,200,100,0.3)', cursor: 'pointer' }}>Kg PI{getSortArrow(sortMI, 'kg')}</th>
                                            <th onClick={() => handleSortMI('unidades')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Ud. PI{getSortArrow(sortMI, 'unidades')}</th>
                                            <th onClick={() => handleSortMI('ubicacion')} style={{ ...thStyle, cursor: 'pointer' }}>Ubicación{getSortArrow(sortMI, 'ubicacion')}</th>
                                            <th onClick={() => handleSortMI('freezer')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Freezer / Pallet{getSortArrow(sortMI, 'freezer')}</th>
                                            <th onClick={() => handleSortMI('fecha')} style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }}>Fecha{getSortArrow(sortMI, 'fecha')}</th>
                                            <th style={thStyle}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedMI.length === 0 ? (
                                            <tr>
                                                <td colSpan={10} style={{ padding: '30px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', backgroundColor: '#1a1a1a' }}>
                                                    Sin registros.
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedMI.map((entry, idx) => {
                                                const isSalida = entry.tipo === 'salida';
                                                return (
                                                    <tr key={entry.id}
                                                        style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}
                                                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)')}
                                                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)')}
                                                    >
                                                        <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                                                            {entry.producto}
                                                            {isSalida && <span style={{ marginLeft: '8px', fontSize: '0.65rem', background: '#c62828', color: '#fff', padding: '1px 5px', borderRadius: '4px' }}>SALIDA</span>}
                                                            {isSalida && entry.motivo && <div style={{ fontSize: '0.7rem', color: '#ff8a80', fontStyle: 'italic', fontWeight: 'normal' }}>📌 {entry.motivo}</div>}
                                                        </td>
                                                        <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.5)' }}>{entry.origenProducto}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: isSalida ? '#ffab91' : '#ffd54f' }}><strong>{entry.kgMP}</strong> kg</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: isSalida ? '#ffab91' : '#ffd54f' }}>{entry.unidadesMP} u.</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: isSalida ? '#ffab91' : '#81c784', fontWeight: 600 }}><strong>{entry.kg}</strong> kg</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: isSalida ? '#ffab91' : '#81c784' }}>{entry.unidades} u.</td>
                                                        <td style={tdStyle}>
                                                            <span style={{
                                                                padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                                                                backgroundColor: entry.ubicacion === 'FÁBRICA' ? 'rgba(46,125,50,0.35)' : 'rgba(245,127,23,0.3)',
                                                                color: entry.ubicacion === 'FÁBRICA' ? '#a5d6a7' : '#ffcc80',
                                                            }}>
                                                                {entry.ubicacion}
                                                            </span>
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: '#3498db' }}>{entry.freezer}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>{entry.fecha}</td>
                                                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                            <button
                                                                onClick={() => handleDeleteMI(entry.id)}
                                                                title="Eliminar"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '1rem' }}
                                                            >🗑️</button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ═══════════════════ GESTIÓN DE PRODUCTOS ════════════════════ */}
            {view === 'crud' && (
                <div>
                    {/* Sub-tabs */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                        {(['mp', 'mi'] as const).map(s => (
                            <button
                                key={s}
                                onClick={() => setCrudSection(s)}
                                style={{
                                    padding: '8px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                                    fontWeight: 'bold', fontSize: '0.88rem',
                                    backgroundColor: crudSection === s ? '#f39c12' : 'rgba(255,255,255,0.1)',
                                    color: crudSection === s ? '#000' : 'rgba(255,255,255,0.7)',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {s === 'mp' ? 'Materia Prima' : 'Materia Intermedia'}
                            </button>
                        ))}
                    </div>

                    {/* ── CRUD Materia Prima ── */}
                    {crudSection === 'mp' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                            {/* Categorías — full width */}
                            <div style={{ gridColumn: '1 / -1', background: 'rgba(0,0,0,0.25)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', padding: '18px' }}>
                                <h4 style={{ margin: '0 0 14px', color: '#f39c12', fontSize: '0.95rem' }}>Categorías</h4>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                                    <input
                                        type="text" value={newCatName}
                                        onChange={e => setNewCatName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddCatMP())}
                                        placeholder="Nueva categoría (ej: CERDO)..."
                                        style={{ ...inputStyle, flex: '1 1 220px', maxWidth: '320px' }}
                                    />
                                    <button onClick={handleAddCatMP} style={{ padding: '8px 14px', backgroundColor: '#f39c12', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                        + Agregar
                                    </button>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    {categoriasMP.map(c => (
                                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,0.07)', borderRadius: '20px', padding: '4px 10px 4px 14px', border: '1px solid rgba(255,255,255,0.12)' }}>
                                            {editCatId === c.id ? (
                                                <>
                                                    <input
                                                        type="text" value={editCatName}
                                                        onChange={e => setEditCatName(e.target.value)}
                                                        onKeyDown={e => e.key === 'Enter' && handleSaveCatMP(c.id)}
                                                        autoFocus
                                                        style={{ background: 'transparent', border: 'none', outline: 'none', color: '#f8f9fa', fontWeight: 700, fontSize: '0.85rem', width: '100px' }}
                                                    />
                                                    <button onClick={() => handleSaveCatMP(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '2px' }}>✅</button>
                                                    <button onClick={() => { setEditCatId(null); setEditCatName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', padding: '2px' }}>❌</button>
                                                </>
                                            ) : (
                                                <>
                                                    <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#f8f9fa' }}>{c.nombre}</span>
                                                    <button onClick={() => { setEditCatId(c.id); setEditCatName(c.nombre); }} title="Renombrar" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '2px', opacity: 0.6 }}>✏️</button>
                                                    <button onClick={() => handleDeleteCatMP(c.id)} title="Eliminar" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '2px', opacity: 0.6 }}>🗑️</button>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Subcategorías */}
                            <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', padding: '18px' }}>
                                <h4 style={{ margin: '0 0 14px', color: '#f39c12', fontSize: '0.95rem' }}>Subcategorías</h4>

                                {/* Filtro categoría */}
                                <div style={{ marginBottom: '12px' }}>
                                    <label style={labelStyle}>Categoría</label>
                                    <select value={crudCatFilter} onChange={e => setCrudCatFilter(e.target.value as Categoria)} style={selectStyle}>
                                        {categoriasMP.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                    </select>
                                </div>

                                {/* Agregar */}
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                                    <input
                                        type="text" value={newSubcatName}
                                        onChange={e => setNewSubcatName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddSubcatMP())}
                                        placeholder="Nueva subcategoría..."
                                        style={{ ...inputStyle, flex: 1 }}
                                    />
                                    <button onClick={handleAddSubcatMP} style={{ padding: '8px 14px', backgroundColor: '#f39c12', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                        + Agregar
                                    </button>
                                </div>

                                {/* Lista */}
                                <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Nombre</th>
                                                <th style={{ ...thStyle, width: '40px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subcatsMP.filter(s => s.categoria === crudCatFilter).length === 0 ? (
                                                <tr><td colSpan={2} style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Sin subcategorías</td></tr>
                                            ) : (
                                                subcatsMP.filter(s => s.categoria === crudCatFilter).map((s, idx) => (
                                                    <tr key={s.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                                        <td style={tdStyle}>{s.nombre}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                            <button onClick={() => handleDeleteSubcatMP(s.id)} title="Eliminar" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>🗑️</button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Productos MP */}
                            <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', padding: '18px' }}>
                                <h4 style={{ margin: '0 0 14px', color: '#f39c12', fontSize: '0.95rem' }}>Productos / Cortes</h4>

                                {/* Filtros */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                                    <div>
                                        <label style={labelStyle}>Categoría</label>
                                        <select value={crudProdCat} onChange={e => { setCrudProdCat(e.target.value as Categoria); setCrudProdSubcat(''); }} style={selectStyle}>
                                            {categoriasMP.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={labelStyle}>Subcategoría</label>
                                        <select value={crudProdSubcat} onChange={e => setCrudProdSubcat(e.target.value)} style={selectStyle}>
                                            <option value="">-- Todas --</option>
                                            {subcatsForCrudProd.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Agregar */}
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                                    <input
                                        type="text" value={newProdMpName}
                                        onChange={e => setNewProdMpName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddProdMP())}
                                        placeholder={crudProdSubcat ? `Nuevo producto en ${crudProdSubcat}...` : 'Seleccioná subcategoría primero'}
                                        disabled={!crudProdSubcat}
                                        style={{ ...inputStyle, flex: 1, opacity: crudProdSubcat ? 1 : 0.5 }}
                                    />
                                    <button onClick={handleAddProdMP} disabled={!crudProdSubcat} style={{ padding: '8px 14px', backgroundColor: crudProdSubcat ? '#f39c12' : 'rgba(255,255,255,0.1)', color: crudProdSubcat ? '#000' : 'rgba(255,255,255,0.4)', border: 'none', borderRadius: '6px', cursor: crudProdSubcat ? 'pointer' : 'not-allowed', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                        + Agregar
                                    </button>
                                </div>

                                {/* Lista */}
                                <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', maxHeight: '340px', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Subcategoría</th>
                                                <th style={thStyle}>Producto</th>
                                                <th style={{ ...thStyle, width: '80px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productosMP.filter(p => p.categoria === crudProdCat && (!crudProdSubcat || p.subcategoria === crudProdSubcat)).length === 0 ? (
                                                <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Sin productos</td></tr>
                                            ) : (
                                                productosMP.filter(p => p.categoria === crudProdCat && (!crudProdSubcat || p.subcategoria === crudProdSubcat)).map((p, idx) => (
                                                    <tr key={p.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                                        <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem' }}>{p.subcategoria}</td>
                                                        <td style={tdStyle}>
                                                            {editProdMpId === p.id ? (
                                                                <input
                                                                    type="text" value={editProdMpName}
                                                                    onChange={e => setEditProdMpName(e.target.value)}
                                                                    onKeyDown={e => e.key === 'Enter' && handleSaveProdMP(p.id)}
                                                                    autoFocus
                                                                    style={{ ...inputStyle, padding: '4px 8px', fontSize: '0.85rem' }}
                                                                />
                                                            ) : p.nombre}
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            {editProdMpId === p.id ? (
                                                                <>
                                                                    <button onClick={() => handleSaveProdMP(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>✅</button>
                                                                    <button onClick={() => { setEditProdMpId(null); setEditProdMpName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>❌</button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button onClick={() => { setEditProdMpId(p.id); setEditProdMpName(p.nombre); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>✏️</button>
                                                                    <button onClick={() => handleDeleteProdMP(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>🗑️</button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── CRUD Materia Intermedia ── */}
                    {crudSection === 'mi' && (
                        <div style={{ maxWidth: '560px' }}>
                            <div style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', padding: '18px' }}>
                                <h4 style={{ margin: '0 0 14px', color: '#f39c12', fontSize: '0.95rem' }}>Productos de Materia Intermedia</h4>

                                {/* Agregar */}
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                                    <input
                                        type="text" value={newProdMiName}
                                        onChange={e => setNewProdMiName(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddProdMI())}
                                        placeholder="Nuevo producto intermedio..."
                                        style={{ ...inputStyle, flex: 1 }}
                                    />
                                    <button onClick={handleAddProdMI} style={{ padding: '8px 14px', backgroundColor: '#f39c12', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                        + Agregar
                                    </button>
                                </div>

                                {/* Lista */}
                                <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>Producto</th>
                                                <th style={{ ...thStyle, width: '80px' }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {productosMI.length === 0 ? (
                                                <tr><td colSpan={2} style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>Sin productos</td></tr>
                                            ) : (
                                                productosMI.map((p, idx) => (
                                                    <tr key={p.id} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)' }}>
                                                        <td style={tdStyle}>
                                                            {editProdMiId === p.id ? (
                                                                <input
                                                                    type="text" value={editProdMiName}
                                                                    onChange={e => setEditProdMiName(e.target.value)}
                                                                    onKeyDown={e => e.key === 'Enter' && handleSaveProdMI(p.id)}
                                                                    autoFocus
                                                                    style={{ ...inputStyle, padding: '4px 8px', fontSize: '0.85rem' }}
                                                                />
                                                            ) : p.nombre}
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                            {editProdMiId === p.id ? (
                                                                <>
                                                                    <button onClick={() => handleSaveProdMI(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>✅</button>
                                                                    <button onClick={() => { setEditProdMiId(null); setEditProdMiName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>❌</button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button onClick={() => { setEditProdMiId(p.id); setEditProdMiName(p.nombre); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>✏️</button>
                                                                    <button onClick={() => handleDeleteProdMI(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>🗑️</button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════ MODAL EDITAR MP ═══════════════════════ */}
            {showEditModal && (
                <div
                    onClick={cancelEditMP}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 2000,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '20px',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            backgroundColor: '#3a0d15',
                            border: '1px solid rgba(243,156,18,0.4)',
                            borderRadius: '10px',
                            padding: '28px',
                            width: '100%',
                            maxWidth: '680px',
                            boxShadow: '0 20px 60px rgba(0,0,0,0.7)',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, color: '#f39c12', fontSize: '1.1rem' }}>✏️ Editar Entrada</h3>
                            <button
                                onClick={cancelEditMP}
                                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}
                            >×</button>
                        </div>

                        <form onSubmit={handleSaveModal}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px', alignItems: 'end' }}>

                                <div>
                                    <label style={labelStyle}>Categoría</label>
                                    <select
                                        value={modalCategoria}
                                        onChange={e => { setModalCategoria(e.target.value as Categoria); setModalSubcat(''); setModalProducto(''); }}
                                        style={selectStyle}
                                        required
                                    >
                                        {categoriasMP.map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label style={labelStyle}>Subcategoría</label>
                                    <select
                                        value={modalSubcat}
                                        onChange={e => { setModalSubcat(e.target.value); setModalProducto(''); }}
                                        style={selectStyle}
                                        required
                                    >
                                        <option value="">-- Seleccionar --</option>
                                        {subcatsForModal.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label style={labelStyle}>Producto / Corte</label>
                                    <select
                                        value={modalProducto}
                                        onChange={e => setModalProducto(e.target.value)}
                                        style={selectStyle}
                                        required
                                    >
                                        <option value="">-- Seleccionar --</option>
                                        {productosForModal.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label style={labelStyle}>Kg</label>
                                    <input
                                        type="number" value={modalKg}
                                        onChange={e => setModalKg(e.target.value)}
                                        min="0" step="0.01" placeholder="0.00"
                                        style={inputStyle}
                                    />
                                </div>

                                <div>
                                    <label style={labelStyle}>Unidades</label>
                                    <input
                                        type="number" value={modalUnidades}
                                        onChange={e => setModalUnidades(e.target.value)}
                                        min="0" placeholder="0"
                                        style={inputStyle}
                                    />
                                </div>

                                <div>
                                    <label style={labelStyle}>Ubicación</label>
                                    <select
                                        value={modalUbicacion}
                                        onChange={e => { setModalUbicacion(e.target.value as Ubicacion); setModalFreezer(''); }}
                                        style={selectStyle}
                                        required
                                    >
                                        <option value="FÁBRICA">FÁBRICA</option>
                                        <option value="FRIGORÍFICO GABOTO">FRIGORÍFICO GABOTO</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={labelStyle}>{modalUbicacion === 'FÁBRICA' ? 'Freezer' : 'Pallet'}</label>
                                    <select
                                        value={modalFreezer}
                                        onChange={e => setModalFreezer(e.target.value)}
                                        style={selectStyle}
                                        required
                                    >
                                        <option value="">-- Seleccionar --</option>
                                        {freezerOptionsModal.map(f => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                </div>

                                {modalUbicacion === 'FRIGORÍFICO GABOTO' && (
                                    <div>
                                        <label style={labelStyle}>Caja (1-10)</label>
                                        <select
                                            value={modalCaja}
                                            onChange={e => setModalCaja(e.target.value)}
                                            style={selectStyle}
                                            required
                                        >
                                            <option value="">-- Seleccionar --</option>
                                            {[...Array(10)].map((_, i) => (
                                                <option key={i + 1} value={i + 1}>Caja {i + 1}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '12px', marginTop: '22px', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={cancelEditMP}
                                    style={{ padding: '10px 24px', backgroundColor: 'rgba(255,255,255,0.08)', color: '#f8f9fa', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    style={{ padding: '10px 24px', backgroundColor: '#f39c12', color: '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                >
                                    Guardar cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockFabrica;
